// ═══════════════════════════════════════════════════════════════════
//  ATLAS LOADER — Parse, validate, and index atlas manifests.
// ═══════════════════════════════════════════════════════════════════
//
//  Pure functions only — no React, no fetch, no localStorage. This
//  file is unit-test-friendly: feed it JSON, get back a validated
//  Atlas + reverse-index. Side-effects (loading from URL, caching)
//  belong in `useAtlas.ts`.
//
//  See `documents/v1/ATLAS.md` for architecture rationale.
//
// ═══════════════════════════════════════════════════════════════════

import type {
  Atlas,
  AtlasCategory,
  AtlasDocument,
  AtlasSearchEntry,
  AtlasValidationError,
  AtlasValidationResult,
  AtlasValidationWarning,
  CrossDocumentRef,
  ReverseRefIndex,
  SheetSummary,
} from '@/types/atlas';
import { isAtlas } from '@/types/atlas';
import { detectOrphans } from './atlas/orphans';

// ───────────────────────────────────────────────────────────────────
//  Parsing
// ───────────────────────────────────────────────────────────────────

/**
 * Parse a raw JSON string into an Atlas. Throws on invalid JSON;
 * returns validation result for semantic errors so callers can decide
 * whether to load with warnings or refuse.
 */
export function parseAtlas(raw: string): {
  atlas: Atlas | null;
  validation: AtlasValidationResult;
} {
  let obj: unknown;
  try {
    obj = JSON.parse(raw);
  } catch (e) {
    return {
      atlas: null,
      validation: {
        valid: false,
        errors: [{
          code: 'invalid-schema-version',
          message: `Invalid JSON: ${(e as Error).message}`,
        }],
        warnings: [],
      },
    };
  }

  if (!isAtlas(obj)) {
    return {
      atlas: null,
      validation: {
        valid: false,
        errors: [{
          code: 'invalid-schema-version',
          message: 'Object does not match Atlas schema (missing schemaVersion=1, or required arrays).',
        }],
        warnings: [],
      },
    };
  }

  const validation = validateAtlas(obj);
  // Even with errors we return the parsed atlas so UIs can show partial data
  // alongside warnings — callers decide whether to render or block.
  return { atlas: obj, validation };
}

// ───────────────────────────────────────────────────────────────────
//  Validation
// ───────────────────────────────────────────────────────────────────

/**
 * Validate an Atlas manifest semantically. Pure function.
 *
 * Errors block usage (e.g., duplicate IDs).
 * Warnings are non-blocking (e.g., orphan documents).
 */
export function validateAtlas(atlas: Atlas): AtlasValidationResult {
  const errors: AtlasValidationError[] = [];
  const warnings: AtlasValidationWarning[] = [];

  // Schema version
  if (atlas.schemaVersion !== 1) {
    errors.push({
      code: 'invalid-schema-version',
      message: `Unsupported schemaVersion=${atlas.schemaVersion}; expected 1.`,
      path: '/schemaVersion',
    });
  }

  // Duplicate document IDs
  const docIds = new Set<string>();
  atlas.documents.forEach((doc, i) => {
    if (docIds.has(doc.id)) {
      errors.push({
        code: 'duplicate-document-id',
        message: `Duplicate document id: ${doc.id}`,
        path: `/documents/${i}/id`,
      });
    }
    docIds.add(doc.id);
  });

  // Duplicate category IDs
  const catIds = new Set<string>();
  atlas.categories.forEach((cat, i) => {
    if (catIds.has(cat.id)) {
      errors.push({
        code: 'duplicate-category-id',
        message: `Duplicate category id: ${cat.id}`,
        path: `/categories/${i}/id`,
      });
    }
    catIds.add(cat.id);
  });

  // Documents reference unknown categories
  atlas.documents.forEach((doc, i) => {
    if (!catIds.has(doc.category)) {
      errors.push({
        code: 'unknown-category',
        message: `Document "${doc.title}" references unknown category: ${doc.category}`,
        path: `/documents/${i}/category`,
      });
    }
  });

  // Refs must point to known documents
  const seenRefKeys = new Set<string>();
  atlas.crossDocumentRefs.forEach((ref, i) => {
    if (!docIds.has(ref.fromDocumentId)) {
      errors.push({
        code: 'unknown-document-in-ref',
        message: `Ref ${ref.id} fromDocumentId not found: ${ref.fromDocumentId}`,
        path: `/crossDocumentRefs/${i}/fromDocumentId`,
      });
    }
    if (!docIds.has(ref.toDocumentId)) {
      errors.push({
        code: 'unknown-document-in-ref',
        message: `Ref ${ref.id} toDocumentId not found: ${ref.toDocumentId}`,
        path: `/crossDocumentRefs/${i}/toDocumentId`,
      });
    }

    // Self-refs (same doc, same block) are usually mistakes.
    if (
      ref.fromDocumentId === ref.toDocumentId &&
      ref.fromBlockId &&
      ref.fromBlockId === ref.toBlockId
    ) {
      errors.push({
        code: 'self-ref',
        message: `Ref ${ref.id} points to itself.`,
        path: `/crossDocumentRefs/${i}`,
      });
    }

    // Duplicate refs (same from/to/type) are warnings, not errors.
    const key = `${ref.fromDocumentId}/${ref.fromBlockId ?? ''}->${ref.toDocumentId}/${ref.toBlockId ?? ''}:${ref.type}`;
    if (seenRefKeys.has(key)) {
      warnings.push({
        code: 'duplicate-ref',
        message: `Duplicate ref (same from/to/type): ${ref.id}`,
        path: `/crossDocumentRefs/${i}`,
      });
    }
    seenRefKeys.add(key);
  });

  // Orphan / isolation detection lives in `src/lib/atlas/orphans.ts`
  // so it can be reused standalone (CLI `--orphans-only`, memoised
  // hooks, etc.). validateAtlas just forwards.
  const orphanReport = detectOrphans(atlas);
  warnings.push(...orphanReport.warnings);

  // Unused categories
  const usedCategories = new Set(atlas.documents.map((d) => d.category));
  atlas.categories.forEach((cat) => {
    if (!usedCategories.has(cat.id)) {
      warnings.push({
        code: 'unused-category',
        message: `Category "${cat.label}" (${cat.id}) has zero documents.`,
        path: `/categories/${atlas.categories.indexOf(cat)}`,
      });
    }
  });

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

// ───────────────────────────────────────────────────────────────────
//  Reverse-index — built once, queried often
// ───────────────────────────────────────────────────────────────────

/**
 * Build a reverse-index: documentId → refs that point TO that document.
 *
 * Used to answer "Which documents reference Document X?" in O(1) after
 * the index is built. Building is O(n) where n = number of refs.
 *
 * Pure function — safe to memoize on `atlas.crossDocumentRefs` reference.
 */
export function buildReverseRefIndex(refs: readonly CrossDocumentRef[]): ReverseRefIndex {
  const index: ReverseRefIndex = new Map();
  for (const ref of refs) {
    if (!index.has(ref.toDocumentId)) {
      index.set(ref.toDocumentId, []);
    }
    index.get(ref.toDocumentId)!.push(ref);

    // Bidirectional refs index in both directions.
    if (ref.bidirectional) {
      if (!index.has(ref.fromDocumentId)) {
        index.set(ref.fromDocumentId, []);
      }
      index.get(ref.fromDocumentId)!.push(ref);
    }
  }
  return index;
}

/**
 * Build a forward-index: documentId → refs that originate FROM that document.
 * Mirror of `buildReverseRefIndex`.
 */
export function buildForwardRefIndex(refs: readonly CrossDocumentRef[]): ReverseRefIndex {
  const index: ReverseRefIndex = new Map();
  for (const ref of refs) {
    if (!index.has(ref.fromDocumentId)) {
      index.set(ref.fromDocumentId, []);
    }
    index.get(ref.fromDocumentId)!.push(ref);

    if (ref.bidirectional) {
      if (!index.has(ref.toDocumentId)) {
        index.set(ref.toDocumentId, []);
      }
      index.get(ref.toDocumentId)!.push(ref);
    }
  }
  return index;
}

// ───────────────────────────────────────────────────────────────────
//  Search index — used by workspace browser search bar
// ───────────────────────────────────────────────────────────────────

/**
 * Build a flat searchable index across ALL blocks in ALL documents
 * of an atlas.
 *
 * Requires sheet content (Layer 3) to be loaded — call this after
 * `loadAllSheets()` in `useAtlas`.
 *
 * The index is rebuilt when documents change. For atlases with
 * thousands of blocks, prefer Web-Worker-based building.
 */
export function buildSearchIndex(
  atlas: Atlas,
  sheetContents: Map<string, ReadonlyArray<{ id: string; name: string; type: string; description?: string; sheetId: string; sheetTitle: string }>>,
): AtlasSearchEntry[] {
  const entries: AtlasSearchEntry[] = [];

  for (const doc of atlas.documents) {
    const blocks = sheetContents.get(doc.id);
    if (!blocks) continue; // skeleton only — skip until loaded

    for (const block of blocks) {
      const searchText = [
        doc.title,
        block.sheetTitle,
        block.name,
        block.type,
        block.description ?? '',
        ...(doc.tags ?? []),
      ].join(' ').toLowerCase();

      entries.push({
        documentId: doc.id,
        documentTitle: doc.title,
        sheetId: block.sheetId,
        sheetTitle: block.sheetTitle,
        blockId: block.id,
        blockName: block.name,
        blockType: block.type,
        blockDescription: block.description,
        searchText,
      });
    }
  }

  return entries;
}

/**
 * Query a search index. Case-insensitive substring match.
 * For atlases >10k blocks, replace with a proper inverted index.
 */
export function searchAtlas(
  index: AtlasSearchEntry[],
  query: string,
  limit = 50,
): AtlasSearchEntry[] {
  if (!query.trim()) return [];
  const q = query.toLowerCase().trim();
  const results: AtlasSearchEntry[] = [];
  for (const entry of index) {
    if (entry.searchText.includes(q)) {
      results.push(entry);
      if (results.length >= limit) break;
    }
  }
  return results;
}

// ───────────────────────────────────────────────────────────────────
//  Document lookup helpers
// ───────────────────────────────────────────────────────────────────

/** Find a document by ID. Returns null if not found. */
export function findDocument(atlas: Atlas, documentId: string): AtlasDocument | null {
  return atlas.documents.find((d) => d.id === documentId) ?? null;
}

/** Find a category by ID. Returns null if not found. */
export function findCategory(atlas: Atlas, categoryId: string): AtlasCategory | null {
  return atlas.categories.find((c) => c.id === categoryId) ?? null;
}

/** Group documents by category. Returns map keyed by category ID. */
export function groupDocumentsByCategory(atlas: Atlas): Map<string, AtlasDocument[]> {
  const map = new Map<string, AtlasDocument[]>();
  for (const cat of atlas.categories) {
    map.set(cat.id, []);
  }
  for (const doc of atlas.documents) {
    if (!map.has(doc.category)) {
      map.set(doc.category, []);
    }
    map.get(doc.category)!.push(doc);
  }
  // Sort within each category by sortOrder (default 0) then title.
  for (const docs of map.values()) {
    docs.sort((a, b) => {
      const orderDiff = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
      return orderDiff !== 0 ? orderDiff : a.title.localeCompare(b.title);
    });
  }
  return map;
}

/** Sort categories by sortOrder (default 0), then label. */
export function sortCategories(categories: readonly AtlasCategory[]): AtlasCategory[] {
  return [...categories].sort((a, b) => {
    const orderDiff = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    return orderDiff !== 0 ? orderDiff : a.label.localeCompare(b.label);
  });
}

/**
 * Get the refs that reference a specific block. Requires reverse-index.
 * Returns empty array if no refs point to this block.
 */
export function getRefsToBlock(
  reverseIndex: ReverseRefIndex,
  documentId: string,
  blockId: string,
): CrossDocumentRef[] {
  const docRefs = reverseIndex.get(documentId) ?? [];
  return docRefs.filter((r) => r.toBlockId === blockId);
}

/** Get total block count summed across all sheets in a document. */
export function totalBlockCount(doc: AtlasDocument): number {
  return doc.sheets.reduce((sum, s) => sum + s.blockCount, 0);
}

/** Aggregate stats for the atlas — useful for browser header. */
export interface AtlasStats {
  documentCount: number;
  sheetCount: number;
  blockCount: number;
  refCount: number;
  categoryCount: number;
}

export function computeAtlasStats(atlas: Atlas): AtlasStats {
  let sheetCount = 0;
  let blockCount = 0;
  for (const doc of atlas.documents) {
    sheetCount += doc.sheets.length;
    blockCount += totalBlockCount(doc);
  }
  return {
    documentCount: atlas.documents.length,
    sheetCount,
    blockCount,
    refCount: atlas.crossDocumentRefs.length,
    categoryCount: atlas.categories.length,
  };
}

// ───────────────────────────────────────────────────────────────────
//  Skeleton extraction (Layer 2 — derived from full document)
// ───────────────────────────────────────────────────────────────────

/**
 * Given a full multi-sheet document loaded from disk/network, extract
 * the skeleton metadata that lives in the manifest's `sheets` array.
 *
 * Used by the manifest generator script to populate `sheets` field.
 */
export function extractSheetSummaries(
  multiSheetWorkflow: {
    sheets: ReadonlyArray<{
      id: string;
      name: string;
      blocks: { length: number };
      connectors: { length: number };
      groups: { length: number };
    }>;
  },
): SheetSummary[] {
  return multiSheetWorkflow.sheets.map((sheet) => ({
    id: sheet.id,
    title: sheet.name || 'Untitled',
    blockCount: sheet.blocks.length,
    connectorCount: sheet.connectors.length,
    groupCount: sheet.groups.length,
  }));
}

// ───────────────────────────────────────────────────────────────────
//  Resolution helpers — turn refs into actionable navigation targets
// ───────────────────────────────────────────────────────────────────

// ───────────────────────────────────────────────────────────────────
//  HEAD pre-flight — surface unreachable sheet files as warnings
// ───────────────────────────────────────────────────────────────────

export interface SheetReachabilityCheckArgs {
  atlas: Atlas;
  /**
   * Resolve a sheet's relative path to an absolute fetch URL. Caller
   * provides because the resolver lives in atlasFileResolver.ts which
   * depends on AtlasSourceContext.
   */
  resolveUrl: (relativePath: string) => string | null;
  /** Optional caller-supplied fetcher (used by tests). */
  fetch?: typeof globalThis.fetch;
  /** Limit per-batch concurrency. Defaults to 6. */
  concurrency?: number;
  signal?: AbortSignal;
}

/**
 * Issue a HEAD request for every sheet file referenced by the atlas
 * (deduped by URL). Returns a list of warnings for files that 4xx/5xx
 * or fail to fetch outright. Pure with respect to validation — call
 * after validateAtlas if you want the structural + reachability
 * picture in one place.
 *
 * Safe to skip in offline/local contexts: pass a resolveUrl that
 * returns null for every path and you get an empty array.
 */
export async function checkSheetReachability(
  args: SheetReachabilityCheckArgs,
): Promise<AtlasValidationWarning[]> {
  const { atlas, resolveUrl, signal } = args;
  const fetchImpl = args.fetch ?? globalThis.fetch;
  if (typeof fetchImpl !== 'function') return [];

  // Build a unique URL → (documentId, sheetIndex) map so a missing
  // file gets one warning instead of N.
  const targets = new Map<string, { documentId: string; sheetIndex: number; relPath: string }>();
  atlas.documents.forEach((doc) => {
    doc.sheets.forEach((sheet, idx) => {
      const path = sheet.file ?? doc.file;
      if (!path) return;
      const url = resolveUrl(path);
      if (!url) return;
      if (!targets.has(url)) {
        targets.set(url, { documentId: doc.id, sheetIndex: idx, relPath: path });
      }
    });
  });

  const entries = Array.from(targets.entries());
  const concurrency = Math.max(1, Math.min(args.concurrency ?? 6, 16));
  const warnings: AtlasValidationWarning[] = [];

  let cursor = 0;
  async function worker() {
    while (cursor < entries.length) {
      if (signal?.aborted) return;
      const idx = cursor++;
      const [url, info] = entries[idx];
      try {
        const res = await fetchImpl(url, { method: 'HEAD', signal });
        if (!res.ok) {
          warnings.push({
            code: 'sheet-file-unreachable',
            message: `Sheet file "${info.relPath}" not reachable (HTTP ${res.status}) for document ${info.documentId}.`,
            path: `/documents/${atlas.documents.findIndex((d) => d.id === info.documentId)}/sheets/${info.sheetIndex}/file`,
          });
        }
      } catch (e) {
        if (signal?.aborted) return;
        warnings.push({
          code: 'sheet-file-unreachable',
          message: `Sheet file "${info.relPath}" failed pre-flight (${(e as Error).message}) for document ${info.documentId}.`,
          path: `/documents/${atlas.documents.findIndex((d) => d.id === info.documentId)}/sheets/${info.sheetIndex}/file`,
        });
      }
    }
  }

  await Promise.all(Array.from({ length: concurrency }, worker));
  return warnings;
}

/** A resolved ref that the UI can render as a clickable link. */
export interface ResolvedRef {
  ref: CrossDocumentRef;
  fromDocument: AtlasDocument | null;
  toDocument: AtlasDocument | null;
  /** True when both ends resolve to existing documents. */
  intact: boolean;
}

/** Resolve all refs against the atlas. Useful for rendering ref lists. */
export function resolveRefs(atlas: Atlas, refs: readonly CrossDocumentRef[]): ResolvedRef[] {
  return refs.map((ref) => {
    const fromDocument = findDocument(atlas, ref.fromDocumentId);
    const toDocument = findDocument(atlas, ref.toDocumentId);
    return {
      ref,
      fromDocument,
      toDocument,
      intact: fromDocument != null && toDocument != null,
    };
  });
}
